import { suggestedWarmupWeight, warmupPyramid } from './warmup';
import type { SetLog } from '../../shared/types';

/** Warm-ups always come first, then working sets, numbered 1..n. */
function renumber(sets: SetLog[]): SetLog[] {
  const warmups = sets.filter(s => s.warmup);
  const workings = sets.filter(s => !s.warmup);
  return [...warmups, ...workings].map((s, i) => ({ ...s, setNumber: i + 1 }));
}

const blank = (warmup: boolean): SetLog => ({
  setNumber: 0, // renumber() assigns the real value
  warmup,
  completed: false,
});

/**
 * Grows or shrinks one section (warm-up or working) to `count`.
 *
 * Removal always takes from the end and refuses to drop a completed set —
 * losing logged work to a stray tap would be worse than refusing the change.
 * Returns the original array when the change isn't allowed.
 */
export function resizeSection(sets: SetLog[], warmup: boolean, count: number): SetLog[] {
  const section = sets.filter(s => s.warmup === warmup);
  const others = sets.filter(s => s.warmup !== warmup);
  if (count === section.length || count < 0) return sets;

  let next: SetLog[];
  if (count > section.length) {
    const added = Array.from({ length: count - section.length }, () => blank(warmup));
    next = [...section, ...added];
  } else {
    const removing = section.slice(count);
    if (removing.some(s => s.completed)) return sets;
    next = section.slice(0, count);
  }

  return renumber(warmup ? [...next, ...others] : [...others, ...next]);
}

/**
 * Applies a working-set weight and carries it across the rest of the exercise.
 *
 * Setting the weight on a working set propagates it to every other working set
 * that isn't done yet, and back-fills any incomplete warm-up set at that rung's
 * percentage of it (rounded to the nearest 5). Completed sets are never touched
 * — they are a record of what was actually lifted.
 */
export function applyWorkingWeight(
  sets: SetLog[],
  setNumber: number,
  weight: number | undefined,
): SetLog[] {
  const target = sets.find(s => s.setNumber === setNumber);
  if (!target || target.warmup) {
    // Warm-up edits are local: they never drive the working weight.
    return sets.map(s => (s.setNumber === setNumber ? { ...s, weight } : s));
  }

  const warmupCount = sets.filter(s => s.warmup).length;
  const pyramid = warmupPyramid(warmupCount);
  let warmupSeen = 0;

  return sets.map(s => {
    if (s.warmup) {
      const rung = pyramid[warmupSeen];
      warmupSeen += 1;
      if (s.completed || !rung) return s;
      const suggested = weight == null ? undefined : suggestedWarmupWeight(weight, rung.pct) ?? undefined;
      return { ...s, weight: suggested };
    }
    if (s.setNumber === setNumber) return { ...s, weight };
    if (s.completed) return s;
    return { ...s, weight };
  });
}

/** True when a set holds nothing worth keeping. */
const isEmpty = (s: SetLog) => !s.completed && s.weight == null && s.reps == null;

/**
 * Brings a section's set count back inside its bounds.
 *
 * Sessions created before the counts were bounded can hold more sets than the
 * program allows. Rather than let that block every later save, they are healed
 * on load — but only by dropping trailing sets that are empty and incomplete.
 * A set with logged work is never removed, even to satisfy the maximum: real
 * data outranks the prescription.
 */
export function clampSection(
  sets: SetLog[],
  warmup: boolean,
  min: number,
  max: number,
): SetLog[] {
  const section = sets.filter(s => s.warmup === warmup);
  const others = sets.filter(s => s.warmup !== warmup);

  let next = section;
  if (section.length > max) {
    next = [...section];
    while (next.length > max && isEmpty(next[next.length - 1])) next.pop();
  } else if (section.length < min) {
    next = [
      ...section,
      ...Array.from({ length: min - section.length }, () => blank(warmup)),
    ];
  }

  // Compare by length, not identity: the branch above copies the array even
  // when nothing is removable, and returning a fresh array every call would
  // re-trigger the heal effect that calls this — an endless render loop.
  if (next.length === section.length) return sets;
  return renumber(warmup ? [...next, ...others] : [...others, ...next]);
}

/** Applies `clampSection` to both sections of an exercise. */
export function clampSets(
  sets: SetLog[],
  bounds: { warmupMin?: number; warmupMax?: number; workingMin?: number; workingMax?: number },
): SetLog[] {
  const { warmupMin, warmupMax, workingMin, workingMax } = bounds;
  let out = sets;
  if (warmupMin != null && warmupMax != null) out = clampSection(out, true, warmupMin, warmupMax);
  if (workingMin != null && workingMax != null) out = clampSection(out, false, workingMin, workingMax);
  return out;
}

/** Steps a value by `step`, clamping at zero and avoiding float drift. */
export function stepValue(current: number | null | undefined, step: number): number {
  const base = current ?? 0;
  const next = base + step;
  // Weights use 0.5/2.5 increments elsewhere, so round to 2dp to avoid 0.1+0.2.
  return Math.max(0, Math.round(next * 100) / 100);
}


/**
 * Describes what comes next in an exercise: the warm-ups first, then the
 * working sets. Used for the rest timer's label, including when a rest has
 * carried over from the previous exercise and the lifter is about to start
 * this one.
 */
export function nextSetLabel(sets: SetLog[]): string {
  const warmupsLeft = sets.filter(s => s.warmup && !s.completed).length;
  if (warmupsLeft > 0) {
    return `Begin warm-up ${warmupsLeft === 1 ? 'set' : 'sets'}`;
  }
  const workingLeft = sets.filter(s => !s.warmup && !s.completed).length;
  if (workingLeft > 0) {
    return `Begin working ${workingLeft === 1 ? 'set' : 'sets'}`;
  }
  return 'All sets done';
}
